import { GetState, SetState } from "zustand";

import { FilterOptions, GridColumn, IAvailableSummarizeField, SummaryFunction } from "../../../../schemas/report";

import { formatSummarizeByAndAggregationOperations } from "../../utils";

import Config from "../../Config";
import { ReportStore } from "../model";
import { fetchEmployeeFields, fetchEmployeeReportCSV, fetchGroups } from "../requests";

export interface AggregationOperation {
	type: "EMPLOYEE_FIELD" | "REVIEW_FIELD" | "UNIQUE_QUESTION" | "GROUPED_QUESTION";
	name: string;
	operation: SummaryFunction;
}

const employeeReportStore = (set: SetState<ReportStore>, get: GetState<ReportStore>) => ({
	fetchEmployeeReportCSV: async () => {
		const {
			client,
			report,
			getEnabledGridColumns,
			sorts,
			isSummarizeRowsView,
			enabledSummarizeFields,
			getSummaryColumnMetadata,
		} = get();

		if (!client) throw new Error("Client not initialized");
		if (!report) throw new Error("Report not initialized");

		const columns = getEnabledGridColumns().map((column) => {
			const type = column.fieldType;
			const columnID = column.field;

			return {
				type,
				id: columnID,
				fieldValueType: column.fieldValueType,
			};
		});

		const filters = get().getAppliedFilters();

		let aggregationOperations: {
			type: any;
			name: string;
			operation: SummaryFunction;
		}[] = [];
		let summarizeBy = [];
		let summaryColumnMetadata = {};
		let summarizeByMetadata = {};

		if (isSummarizeRowsView) {
			summaryColumnMetadata = getSummaryColumnMetadata();

			const { summarizeBy: formattedSummarizeBy, aggregationOperations: formattedAggregationOperations } =
				formatSummarizeByAndAggregationOperations(enabledSummarizeFields, summaryColumnMetadata);
			summarizeBy = formattedSummarizeBy;
			aggregationOperations = formattedAggregationOperations;

			summarizeByMetadata = enabledSummarizeFields.reduce((acc, field) => {
				acc[field.value] = {
					selection: field.selection,
				};
				return acc;
			}, {});
		}

		const csvJobID = await fetchEmployeeReportCSV(
			client,
			columns,
			filters,
			sorts,
			report.name,
			summarizeBy,
			aggregationOperations,
			isSummarizeRowsView,
			summaryColumnMetadata,
			summarizeByMetadata
		);

		set({ csvJobID });
	},

	fetchAvailableEmployeeColumns: async () => {
		const { client, columnMetadata, report } = get();

		if (!client) throw new Error("Client not initialized");

		const employeeFields = await fetchEmployeeFields(client, report);

		const employeeFieldsAsColumns: GridColumn[] = employeeFields.map((field) => {
			return {
				field: `employee_${field.name}`,
				headerName: field.name,
				sortable: true,
				pinned: null,
				fieldType: "EMPLOYEE_FIELD",
				fieldValueType: field.type,
				fieldPath: ["subject", field.name],
				columnMetadata: columnMetadata[`employee_${field._id}`],
			};
		});

		const additionalEmployeeFieldsAsColumns: GridColumn[] = Object.keys(Config.employeeFields).map((fieldID) => {
			const field = Config.employeeFields[fieldID];

			if (field.name !== "Manager's Email") {
				return {
					field: `employee_${field.name}`,
					headerName: field.headerName,
					sortable: true,
					pinned: null,
					fieldType: "EMPLOYEE_FIELD",
					fieldValueType: field.type,
					fieldPath: ["subject", field.name],
					columnMetadata: columnMetadata[`employee_${field._id}`],
				};
			} else {
				return {
					field: `employee_${field.name}`,
					headerName: field.headerName,
					sortable: true,
					pinned: null,
					fieldType: "EMPLOYEE_FIELD",
					fieldValueType: field.type,
					fieldPath: ["subject", "Manager", "email"],
					columnMetadata: columnMetadata[`employee_${field._id}`],
					valueGetter: (params: any) => {
						const { data } = params;
						// Detail view: Manager is a person object with .email
						const detailValue = data?.subject?.Manager?.email;
						if (detailValue !== null && detailValue !== undefined && detailValue !== "") {
							return detailValue;
						}
						// Summary view: count is at subject["Manager's Email"]
						const summaryValue = data?.subject?.["Manager's Email"];
						if (summaryValue !== undefined) {
							return summaryValue;
						}
						return "";
					},
				};
			}
		});

		// combine and sort custom and additional employee fields
		const allEmployeeFieldsAsColumns = [...employeeFieldsAsColumns, ...additionalEmployeeFieldsAsColumns].sort(
			(a, b) => {
				if (a.headerName < b.headerName) {
					return -1;
				}
				if (a.headerName > b.headerName) {
					return 1;
				}
				return 0;
			}
		);

		return allEmployeeFieldsAsColumns;
	},

	getAvailableEmployeeSummarizeFields: (): IAvailableSummarizeField[] => {
		// static employee fields
		const staticEmployeeFields = Config.summarizeFields.employee;

		// custom numeric employee fields
		const numericEmployeeFields = get()
			.availableGridColumns.map((field) => {
				if (
					field.fieldType === "EMPLOYEE_FIELD" &&
					field.fieldValueType &&
					["monetary", "numeric", "salary", "text"].includes(field.fieldValueType)
				) {
					return {
						value: field.field,
						label: field.headerName,
						group: "EMPLOYEE_FIELD",
						type: field.fieldValueType,
					};
				}
				return null;
			})
			.filter((field) => field !== null);

		// combine and sort custom and static employee fields
		const employeeFields = [...staticEmployeeFields, ...numericEmployeeFields]
			.sort((a, b) => {
				if (a && b) {
					const labelA = a.label.toUpperCase();
					const labelB = b.label.toUpperCase();
					if (labelA < labelB) {
						return -1;
					}
					if (labelA > labelB) {
						return 1;
					}
				}
				return 0;
			})
			.filter((field) => field !== null) as IAvailableSummarizeField[];

		// filter out nulls
		return employeeFields;
	},

	fetchEmployeeFilterOptions: async (): Promise<FilterOptions> => {
		const { client } = get();

		if (!client) throw new Error("Client not initialized");

		const groups = await fetchGroups(client);

		const department = groups.map((group) => {
			return {
				value: group._id,
				label: group.name,
			};
		});

		return {
			department,
		};
	},
});

export default employeeReportStore;
